import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Badge, Alert, Spinner, Modal } from 'react-bootstrap';
import { Play, RefreshCw, X } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import { API_BASE_URL } from '../config/api';

// Laid out like ChoirUpcomingServicesPage, but looking back: each recorded Sunday's music,
// one clip per piece, filed each week from the 10:00 service stream.
interface Piece {
  number: number;
  placement: string;
  title: string;
  file: string;
}

interface RecordedSunday {
  date: string;
  liturgy_name: string | null;
  pieces: Piece[];
}

const ServiceRecordingsPage: React.FC = () => {
  const [sundays, setSundays] = useState<RecordedSunday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    fetchRecordings();
  }, []);

  const fetchRecordings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/service-recordings');
      setSundays(response.data?.data?.sundays || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const pieceName = (piece: Piece) => piece.title || piece.placement;

  const handlePlay = (sunday: RecordedSunday, piece: Piece) => {
    // A video player cannot send the sign-in header, so the token goes in the address.
    const token = localStorage.getItem('token') || '';
    const url = `${API_BASE_URL.replace('/api', '')}/api/service-recordings/${sunday.date}/` +
      `${encodeURIComponent(piece.file)}?token=${encodeURIComponent(token)}`;
    setPlaying({ url, title: pieceName(piece) });
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <Container className="mt-4">
          <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <Spinner animation="border" className="mb-3" />
              <p>Loading recordings...</p>
            </div>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <Container className="mt-4">
        <div className="d-flex align-items-center mb-3">
          <h1 className="mb-0">Service Recordings</h1>
          <button className="btn btn-outline-primary btn-sm ms-auto" onClick={fetchRecordings}>
            <RefreshCw size={16} className="me-1" />
            Refresh
          </button>
        </div>

        <div className="mb-4">
          <p className="text-muted">
            The music from each Sunday's 10:00 service at St. Luke's, one piece at a time.
            A new Sunday appears here on Sunday afternoon.
          </p>
        </div>

        {error ? (
          <Alert variant="danger">
            <strong>Error:</strong> {error}
            <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchRecordings}>
              <RefreshCw size={16} className="me-1" />
              Retry
            </button>
          </Alert>
        ) : sundays.length === 0 ? (
          <Alert variant="info">No recordings yet.</Alert>
        ) : (
          sundays.map((sunday) => (
            <Card key={sunday.date} className="mb-4 shadow-sm">
              <Card.Header className="bg-dark text-white">
                <h5 className="mb-0">
                  {formatDate(sunday.date)}
                  {sunday.liturgy_name && ` - ${sunday.liturgy_name}`}
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <Table className="mb-0" striped hover>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '25%' }}>Service Order</th>
                      <th style={{ width: '60%' }}>Music</th>
                      <th style={{ width: '15%', textAlign: 'center' }}>Watch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sunday.pieces.map((piece) => (
                      <tr key={piece.file}>
                        <td>
                          <Badge bg="secondary" className="px-2 py-1">{piece.placement}</Badge>
                        </td>
                        <td className="fw-bold">{piece.title || <span className="text-muted fw-normal">{piece.placement}</span>}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handlePlay(sunday, piece)}
                            title={`Watch ${pieceName(piece)}`}
                          >
                            <Play size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ))
        )}
      </Container>

      <Modal show={!!playing} onHide={() => setPlaying(null)} size="lg" centered className="video-modal">
        <Modal.Header className="bg-dark text-white">
          <Modal.Title>{playing?.title}</Modal.Title>
          <button type="button" className="btn btn-link text-white p-0 ms-auto" onClick={() => setPlaying(null)}>
            <X size={24} />
          </button>
        </Modal.Header>
        <Modal.Body className="p-0 bg-black">
          {playing && (
            <div className="ratio ratio-16x9">
              <video src={playing.url} controls autoPlay playsInline preload="metadata" />
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ServiceRecordingsPage;
